import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Phone, CheckCircle } from 'lucide-react';
import { SITE } from '../../data/site';
import { SectionHead, Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

const PAGE_URL = 'https://biryanify.us/blog/best-indian-restaurant-irving-las-colinas';
const TITLE = 'Best Indian Restaurants Near Irving, TX & Las Colinas | Biryanify';
const DESCRIPTION =
  'Looking for the best restaurant near Irving, TX, Las Colinas, or Valley Ranch? Discover why Biryanify is the top choice for authentic Hyderabadi biryani, mandi, and South Indian tiffins — open now for pickup & dine-in.';

const FAQS = [
  {
    q: 'What is the best Indian restaurant in Las Colinas?',
    a: 'Biryanify stands out as a top-rated Indian restaurant near Las Colinas for its commitment to authentic Hyderabadi dum biryani, cooked slowly under a sealed lid rather than on a steam table. The menu spans Hyderabadi biryani, South Indian tiffins (dosa, idli), Arabian mandi, and North Indian kebabs, all made fresh daily with premium ingredients.',
  },
  {
    q: 'Is Biryanify within walking distance of Irving Mall?',
    a: 'Biryanify is located at 9400 N MacArthur Blvd in Irving, just a short drive from Irving Mall and the Las Colinas business district. While not immediately adjacent, it\'s a quick 5–10 minute trip, making it perfect for a lunch order after shopping or a dinner stop on your way home.',
  },
  {
    q: 'What are the best dishes to order at Biryanify?',
    a: 'The Hyderabadi Chicken Dum Biryani is the signature dish — dum-cooked basmati with marinated chicken, saffron, mint, and fried onion. Also highly recommended are the Al Faham Chicken Mandi (smoke-grilled Arabian-style), Masala Dosa (tawa-crisp with potato), and Chicken 65 (spiced and fried). All come with sides and can be customized for dietary needs.',
  },
  {
    q: 'Can I order online for pickup right now?',
    a: 'Yes. Biryanify accepts online orders for pickup daily from 11:00 AM to 10:00 PM (Sun–Thu) and 11:00 AM to 11:00 PM (Fri–Sat). You can build and pay for your order on the website, or call (214) 377-8283 to place an order by phone.',
  },
  {
    q: 'Does Biryanify offer dine-in service?',
    a: 'Biryanify primarily operates as a pickup and takeout restaurant, optimized for quick, convenient service. Check our Visit & Hours page for the latest details on seating or call directly to confirm current service options.',
  },
  {
    q: 'What makes Hyderabadi biryani different?',
    a: 'Hyderabadi biryani is cooked using the "dum" method: basmati rice and marinated meat are layered raw, sealed under a tight lid, and slow-steamed so every grain absorbs the meat\'s juices and aromatics (saffron, mint, fried onion). This labor-intensive technique creates much deeper, more complex flavor than buffet-style biryani steamed on a warming tray.',
  },
];

function useSeoHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const setMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', DESCRIPTION);
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESCRIPTION, 'property');
    setMeta('og:url', PAGE_URL, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESCRIPTION);

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'blog-jsonld-2';
    ldScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://biryaniify.us/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Best Indian Restaurant in Irving & Las Colinas',
              item: PAGE_URL,
            },
          ],
        },
        {
          '@type': 'Article',
          headline: 'Best Indian Restaurants Near Irving, TX & Las Colinas — Complete Guide',
          description: DESCRIPTION,
          author: { '@type': 'Organization', name: 'Biryanify' },
          publisher: { '@type': 'Organization', name: 'Biryanify' },
          mainEntityOfPage: PAGE_URL,
          url: PAGE_URL,
          datePublished: '2024-12-18',
          dateModified: '2024-12-18',
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      if (canonical) canonical.setAttribute('href', prevCanonical || 'https://biryanify.us/');
      document.getElementById('blog-jsonld-2')?.remove();
    };
  }, []);
}

export default function BestIndianRestaurantIrvingLasColinas() {
  useSeoHead();

  return (
    <div style={{ background: 'var(--color-surface)' }}>
      <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
        <Img
          src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/3480ab73635b.png"
          alt="A vibrant spread of South Indian tiffins including dosa, idli, sambar and coconut chutney served at Biryanify"
          eager
          className="absolute inset-0"
          imgClassName="scale-105"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.6) 0%, rgba(20,13,8,0.25) 45%, rgba(20,13,8,0.92) 100%)' }} />
        <div className="wrap relative pb-12 pt-32 md:pb-16">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Irving, TX & Las Colinas Guide</p>
          <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.2rem, 5.6vw, 3.6rem)', lineHeight: 1.05, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
            Best Indian Restaurants Near Irving, TX &amp; Las Colinas
          </h1>
          <p className="mt-4 max-w-2xl" style={{ fontSize: '1.08rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
            Searching for "restaurant in Irving TX," "restaurant near me open now," or the best biryani in Las Colinas? Here's what you need to know before your next order.
          </p>
        </div>
      </section>

      <article className="wrap py-14 md:py-20 max-w-3xl mx-auto">
        <Reveal>
          <p className="text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
            Irving, Texas—and the adjoining Las Colinas business district—attracts professionals, families, and travelers looking for quality dining without the hassle. Whether you're searching for "restaurant in Irving mall," "best restaurant Las Colinas," or just "restaurant open now," the options can feel overwhelming. This guide cuts through the noise and explains why <Link to="/" className="underline font-semibold">Biryanify</Link> at 9400 N MacArthur Blvd has become a top choice for authentic Hyderabadi biryani, Arabian mandi, and South Indian food in the Irving area.
          </p>
        </Reveal>

        <Reveal delay={80}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Finding the Best Indian Restaurant Near Las Colinas</h2>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            The Las Colinas corridor is home to hundreds of restaurants, but finding one that serves authentic slow-cooked biryani—rather than buffet-style rice dishes—can be tricky. Here's what separates the best from the rest:
          </p>
          <ul className="mt-5 space-y-3 text-ink-2" style={{ lineHeight: 1.7 }}>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <span><strong className="text-ink">Dum cooking method.</strong> Real Hyderabadi biryani isn't made on a steam table. It's sealed and cooked "on dum"—a slow, labor-intensive technique that builds flavor depth.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <span><strong className="text-ink">Multiple regional cuisines.</strong> The best restaurants blend Hyderabadi biryani, South Indian tiffins, Arabian mandi, and kebabs so every craving is covered in one visit.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <span><strong className="text-ink">Convenient location &amp; hours.</strong> Near Irving Mall, the business district, or major roads—and open during lunch and late-night hours so you can order "restaurant near me open now" and get a real answer.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <span><strong className="text-ink">Fresh daily service.</strong> No dishes reheated from the morning—everything is prepared to order.</span>
            </li>
          </ul>
        </Reveal>

        <Reveal delay={100}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Why Biryanify is Irving's Top Choice</h2>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            <Link to="/about" className="underline font-semibold">Biryanify</Link> was built on the belief that biryani shouldn't be rushed. Every order starts with premium basmati rice layered with fresh, marinated meat (or vegetables), whole spices, saffron, mint, and fried onion, then sealed under a tight handi lid and slow-cooked on dum until the rice absorbs every flavor note.
          </p>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            Beyond the signature biryani, Biryanify covers the full spectrum:
          </p>
          <ul className="mt-5 space-y-2 text-ink-2" style={{ lineHeight: 1.7 }}>
            <li><strong className="text-ink">South Indian tiffins:</strong> Crispy masala dosa, fluffy idli, and uthappam served with sambar and chutneys—perfect for a lighter meal or breakfast any time of day.</li>
            <li><strong className="text-ink">Arabian mandi:</strong> Smoke-grilled al faham chicken over saffron-scented rice, bringing Middle Eastern flavors to Irving.</li>
            <li><strong className="text-ink">Appetizers &amp; mains:</strong> Fiery chicken 65, tandoori kebabs, shawarma, and curries ranging from mild to intensely spiced.</li>
            <li><strong className="text-ink">Vegetarian options:</strong> Veg biryani, dosa, paneer curries, and more—so groups with mixed dietary needs can order together.</li>
            <li><strong className="text-ink">Desserts:</strong> Traditional falooda, kulfi, and gulab jamun to end on a sweet note.</li>
          </ul>
        </Reveal>

        <Reveal delay={120}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Location, Hours &amp; "Restaurant Near Me Open Now"</h2>
          <div className="mt-5 card p-6 md:p-7 grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">{SITE.address.line1}</div>
                <div className="text-ink-2">{SITE.address.line2}</div>
                <div className="text-ink-2 text-sm mt-1">N MacArthur Blvd · Valley Ranch · Near Irving Mall &amp; Las Colinas</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">Open daily</div>
                <div className="text-ink-2 text-sm">Sun–Thu · 11:00 AM – 10:00 PM</div>
                <div className="text-ink-2 text-sm">Fri–Sat · 11:00 AM – 11:00 PM</div>
              </div>
            </div>
            <div className="flex items-start gap-3 sm:col-span-2">
              <Phone size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <a href={SITE.phoneHref} className="font-semibold text-ink hover:underline">{SITE.phone}</a>
            </div>
          </div>
          <p className="mt-6 text-ink-2 text-sm italic" style={{ lineHeight: 1.75 }}>
            <strong>Searching "restaurant near me open now"?</strong> If it's between 11 AM and 10 PM any day of the week (11 PM on weekends), Biryanify is ready. Build your order on the <Link to="/menu" className="underline font-semibold">menu page</Link> or call to place an order by phone.
          </p>
        </Reveal>

        <Reveal delay={140}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">How to Order: Online &amp; Pickup</h2>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            Biryanify makes it easy to get great food without the wait:
          </p>
          <ol className="mt-5 space-y-3 text-ink-2 list-decimal list-inside" style={{ lineHeight: 1.7 }}>
            <li><strong className="text-ink">Browse the menu</strong> on the website — filter by diet tag (veg, egg, non-veg) and see prices and descriptions for every dish.</li>
            <li><strong className="text-ink">Build your order</strong> — select your main (biryani, mandi, dosa, curry) and any sides, drinks, or desserts.</li>
            <li><strong className="text-ink">Pay securely online</strong> — all major cards accepted.</li>
            <li><strong className="text-ink">Get a ready time</strong> — most orders are ready in 20–30 minutes.</li>
            <li><strong className="text-ink">Pick up at the kitchen</strong> — located at 9400 N MacArthur Blvd, Irving.</li>
          </ol>
        </Reveal>

        <Reveal delay={160}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            {FAQS.map((f) => (
              <div key={f.q} className="card p-6">
                <h3 className="font-display text-lg text-ink">{f.q}</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={180}>
          <div className="mt-14 card p-8 text-center" style={{ background: 'var(--color-surface-2)' }}>
            <h2 className="font-display text-2xl text-ink">Ready to order?</h2>
            <p className="mt-3 text-ink-2">Start building your order for pickup at our Irving kitchen, or get directions to visit.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/menu" className="btn btn-primary">Order online now <ArrowRight size={16} /></Link>
              <Link to="/visit" className="btn btn-ghost">Get directions</Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-14 pt-8 border-t border-ink-3">
            <p className="text-ink-2 text-sm">
              <strong>Keywords & searches:</strong> Best Indian restaurant Irving TX · Best restaurant Las Colinas · Restaurant in Irving TX · Restaurant in Irving mall · Best restaurant Irving park · Restaurant near me open now · Best restaurant irvington · Restaurant near me dine-in
            </p>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
